/**
 * Check if a hostname is a local/private IP address
 * This is used to detect local development environments
 */
function isLocalHostname(hostname: string): boolean {
  // Check for localhost variants
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true;
  }
  
  // Check for private IP ranges:
  // 192.168.x.x (most common local network)
  // 10.x.x.x (private network)
  // 172.16-31.x.x (private network)
  const privateIpRegex = /^192\.168\.\d+\.\d+$|^10\.\d+\.\d+\.\d+$|^172\.(1[6-9]|2[0-9]|3[0-1])\.\d+\.\d+$/;
  return privateIpRegex.test(hostname);
}

export const baseUrl = (subdomain?: string) => {
  const baseDomain = process.env.NEXT_PUBLIC_DOMAIN || "orquesti.com";
  // Remove protocol if present in baseDomain
  const cleanDomain = baseDomain.replace(/^https?:\/\//, "");
  
  // Check if we're in a local environment
  // Option 1: Explicit environment variable (most secure)
  const isLocalDev = process.env.NEXT_PUBLIC_LOCAL_DEV === 'true';
  
  // Option 2: Detect local hostname (client-side only, as defense in depth)
  let isLocalHostnameDetected = false;
  if (typeof window !== 'undefined') {
    isLocalHostnameDetected = isLocalHostname(window.location.hostname);
  }
  
  // In development mode OR local dev mode OR local hostname detected
  if (process.env.NODE_ENV === "development" || isLocalDev || isLocalHostnameDetected) {
    // If we're in the browser and have a local hostname, use current origin
    if (typeof window !== 'undefined' && isLocalHostnameDetected) {
      return window.location.origin;
    }
    // Fallback for server-side or when window is not available
    return `http://localhost:3000`;
  }
  
  // In production, use subdomains if provided
  if (typeof subdomain === "string" && subdomain.length > 0) {
    return `https://${subdomain}.${cleanDomain}`;
  }
  
  return `https://${cleanDomain}`;
};

// Function to get the current domain for static assets (images, etc.)
export const getCurrentDomain = () => {
  if (typeof window === "undefined") {
    // Server-side, use the base URL
    return baseUrl();
  }

  // Client-side, use the current domain to avoid CORS issues
  return window.location.origin;
};

/**
 * Generates a shared link URL using the current subdomain from the session.
 * This function should be used when generating links that need to use the company's subdomain.
 *
 * @param token - The token or path to append to the base URL (e.g., "budget/abc123")
 * @param subdomain - The subdomain to use (from SessionContext). If not provided, uses baseUrl without subdomain.
 * @returns The full URL with the subdomain prefix
 *
 * @example
 * // From a component with access to SessionContext:
 * const { session } = useSession();
 * const link = generateSharedLink(`view/budget/${token}`, session.subdomain);
 */
export const generateSharedLink = (
  token: string,
  subdomain?: string | null
): string => {
  const base = baseUrl(subdomain || undefined);
  // Ensure token starts with / if it doesn't already
  const path = token.startsWith("/") ? token : `/${token}`;
  return `${base}${path}`;
};
